import {
  faArrowCircleLeft,
  faArrowCircleRight,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import DescriptionIcon from "@mui/icons-material/Description";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
// import RefreshIcon from "@mui/icons-material/Refresh";
import { Box, IconButton } from "@mui/material";
// import CircularProgress from "@mui/material/CircularProgress";
import * as turf from "@turf/turf";
import axios from "axios";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Zap } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  GeoJSON,
  LayerGroup,
  LayersControl,
  MapContainer,
  Marker,
  Polygon,
  Polyline,
  TileLayer,
  Tooltip,
  useMap,
} from "react-leaflet";
import { captureAndGeneratePdf } from "../OpenmeteoJMA/ReportGenerator_LN";
import {
  characteristicColors,
  riskTranslations,
  translations,
} from "../OpenmeteoJMA/Translations_Lightning";
import "./Nowcast.css";
import ImpactedAreaLN from "../OpenmeteoJMA/ImpactedArea_LN";
 
import CommonTable from '../../../common/CommonTable';
import { MapHomeControl, MapInfoControl, MapLegendControl } from "../../../common/MapControls/MapControls";

const backendUrl = process.env.REACT_APP_BACKEND_API_URL_LOCAL;

// Timor-Leste = UTC+9
const TL_OFFSET_HOURS = 9;

const INITIAL_VIEW_STATE = {
  longitude: 125.7275,
  latitude: -8.8742,
  zoom: 7,
};

// ─── ICONS ──────────────────────────────────────────────────────────────────
const makeObsIcon = (color = "black") =>
  L.divIcon({
    html: `<div style="color:${color};display:flex;align-items:center;justify-content:center;opacity:1;">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M13 2 L3 14 L12 14 L11 22 L21 10 L12 10 L13 2 Z"/>
            </svg></div>`,
    className: "custom-lightning-icon",
    iconSize: [20, 20],
    iconAnchor: [10, 10],
  });

const obsIconRed = makeObsIcon("#ef4444");
const obsIconOrange = makeObsIcon("#f97316");
const obsIconYellow = makeObsIcon("#eab308");
const obsIconLightYellow = makeObsIcon("#fde047");
const obsIconGrey = makeObsIcon("#9ca3af");

const centroidLightningIcon = L.divIcon({
  html: `<div style="color:#fbbf24;background:#111827;border-radius:50%;padding:4px;display:flex;align-items:center;justify-content:center;border:2px solid #fbbf24;opacity:1;">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
            <path d="M13 2 L3 14 L12 14 L11 22 L21 10 L12 10 L13 2 Z"/>
          </svg></div>`,
  className: "custom-centroid-icon",
  iconSize: [26, 26],
  iconAnchor: [13, 13],
});

// ─── BUCKET CONFIG ──────────────────────────────────────────────────────────
const BUCKET_CONFIG = {
  "5mins": { color: "#ef4444", label: "Last 5 min", layerName: "Nowcast: Last 5 min", icon: obsIconRed, defaultChecked: true },
  "10-5min": { color: "#f97316", label: "10–5 min ago", layerName: "Nowcast: 10–5 min", icon: obsIconOrange, defaultChecked: true },
  "15-10min": { color: "#ffc003", label: "15–10 min ago", layerName: "Nowcast: 15–10 min", icon: obsIconYellow, defaultChecked: true },
  "30-15min": { color: "#fde047", label: "30–15 min ago", layerName: "Nowcast: 30–15 min", icon: obsIconLightYellow, defaultChecked: true },
  "24hours": { color: "#9ca3af", label: "Past 24 hrs", layerName: "Nowcast: Past 24 hrs", icon: obsIconGrey, defaultChecked: false },
};

const reliabilityColors = {
  HIGH: "rgba(230,45,58,0.4)", High: "rgba(230,45,58,0.4)",
  MEDIUM: "rgba(243,155,58,0.4)", Medium: "rgba(243,155,58,0.4)",
  LOW: "rgba(243,235,18,0.5)", Low: "rgba(243,235,18,0.5)",
  DEFAULT: "rgba(107,114,128,0.4)", "N/A": "rgba(107,114,128,0.4)",
};

const reliabilityBorders = {
  HIGH: "rgb(230,45,58)", High: "rgb(230,45,58)",
  MEDIUM: "rgb(243,155,58)", Medium: "rgb(243,155,58)",
  LOW: "rgb(243,235,18)", Low: "rgb(243,235,18)",
  DEFAULT: "rgb(107,114,128)",
};

// ─── TIME HELPERS ────────────────────────────────────────────────────────────
const getTLNow = () => new Date(Date.now() + TL_OFFSET_HOURS * 3600000);

const floorTo5Min = (d) => {
  const m = Math.floor(d.getUTCMinutes() / 5) * 5;
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), d.getUTCHours(), m, 0, 0));
};

const formatSlotDisplay = (d) => {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getUTCFullYear()}-${p(d.getUTCMonth()+1)}-${p(d.getUTCDate())} T ${p(d.getUTCHours())}:${p(d.getUTCMinutes())}`;
};

const formatSlotForApi = (d) => {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getUTCFullYear()}-${p(d.getUTCMonth()+1)}-${p(d.getUTCDate())}/${p(d.getUTCHours())}:${p(d.getUTCMinutes())}`;
};

// ─── ZOOM HELPER ─────────────────────────────────────────────────────────────
const ZoomToFeature = ({ feature }) => {
  const map = useMap();
  useEffect(() => {
    if (feature?.features?.[0]) {
      map.flyToBounds(L.geoJSON(feature).getBounds(), { padding: [20, 20] });
    }
  }, [feature, map]);
  return null;
};

const getBoundaryStyle = () => ({ color: "black", weight: 1, fillOpacity: 0 });

// ─── BUCKET BADGE ─────────────────────────────────────────────────────────────
const BucketBadge = ({ bucket, total, cg, ic }) => {
  const cfg = BUCKET_CONFIG[bucket];
  if (!cfg) return null;
  return (
    <div style={{
      background: "white",
      border: `2px solid ${cfg.color}`,
      borderRadius: "10px",
      padding: "5px 12px",
      minWidth: "138px",
      boxShadow: `0 2px 6px ${cfg.color}25`,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: "5px", whiteSpace: "nowrap" }}>
        <svg width="11" height="11" viewBox="0 0 24 24" fill={cfg.color} style={{ flexShrink: 0 }}>
          <path d="M13 2 L3 14 L12 14 L11 22 L21 10 L12 10 L13 2 Z" />
        </svg>
        <span style={{ fontSize: "11px", fontWeight: 600, color: "#111" }}>{cfg.label}</span>
        <span style={{ fontSize: "15px", fontWeight: 800, color: cfg.color, marginLeft: "2px" }}>{total}</span>
      </div>
      <div style={{ fontSize: "10px", color: "#666", marginTop: "2px", paddingLeft: "16px" }}>
        CG: <span style={{ color: cfg.color, fontWeight: 700 }}>{cg}</span>
        {" · "}
        IC: <span style={{ color: cfg.color, fontWeight: 700 }}>{ic}</span>
      </div>
    </div>
  );
};

// ─── NOWCAST TABLE COMPONENT WITH ENHANCED FEATURES ─────────────────────────
// ─── NOWCAST TABLE COMPONENT ─────────────────────────
// const NowcastTable = ({ tableData, loading, currentTimestamp, bucketCounts }) => {
//   const [searchTerm, setSearchTerm] = useState("");
//   const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
//   const [currentPage, setCurrentPage] = useState(1);
//   const [rowsPerPage, setRowsPerPage] = useState(10);
//   const [visibleColumns, setVisibleColumns] = useState({
//     interval: true,
//     observation_id: true,
//     adm3: true,           // Suco
//     adm2: true,           // Post Administrative
//     adm1: true,           // Municipality
//     pulse_type: true,
//     peak_amplitude: true,
//     number_sensors: true,
//     lat: true,
//     lng: true,
//     created_at: true,
//     date_hr: true,
//   });

//   // Filter and sort data
//   const filteredData = useMemo(() => {
//     let data = tableData.filter(row => {
//       const searchLower = searchTerm.toLowerCase();
//       return (
//         row.observation_id?.toLowerCase().includes(searchLower) ||
//         row.adm3?.toLowerCase().includes(searchLower) ||
//         row.adm2?.toLowerCase().includes(searchLower) ||
//         row.adm1?.toLowerCase().includes(searchLower) ||
//         row.pulse_type?.toLowerCase().includes(searchLower) ||
//         String(row.peak_amplitude).includes(searchLower) ||
//         row.date_hr?.toLowerCase().includes(searchLower)
//       );
//     });

//     if (sortConfig.key) {
//       data.sort((a, b) => {
//         let aVal = a[sortConfig.key];
//         let bVal = b[sortConfig.key];

//         if (sortConfig.key === "interval") {
//           const order = ["5mins", "10-5min", "15-10min", "30-15min", "24hours"];
//           aVal = order.indexOf(a.bucket);
//           bVal = order.indexOf(b.bucket);
//         }

//         if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
//         if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
//         return 0;
//       });
//     }

//     return data;
//   }, [tableData, searchTerm, sortConfig]);

//   // Pagination
//   const totalPages = Math.ceil(filteredData.length / rowsPerPage);
//   const paginatedData = filteredData.slice(
//     (currentPage - 1) * rowsPerPage,
//     currentPage * rowsPerPage
//   );

//   const handleSort = (key) => {
//     setSortConfig({
//       key,
//       direction: sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc",
//     });
//   };

//   const exportToCSV = () => {
//     const headers = Object.keys(visibleColumns).filter(k => visibleColumns[k]);
//     const csvContent = [
//       headers.join(","),
//       ...filteredData.map(row => {
//         return headers.map(h => {
//           if (h === "interval") {
//             const cfg = BUCKET_CONFIG[row.bucket];
//             return `"${cfg?.label || row.bucket}"`;
//           }
//           return `"${row[h] || ""}"`;
//         }).join(",");
//       })
//     ].join("\n");

//     const blob = new Blob([csvContent], { type: "text/csv" });
//     const url = URL.createObjectURL(blob);
//     const a = document.createElement("a");
//     a.href = url;
//     a.download = `nowcast_data_${currentTimestamp.replace(/[: ]/g, "_")}.csv`;
//     a.click();
//     URL.revokeObjectURL(url);
//   };

//   return (
//     <div style={{ marginTop: "10px" }}>
//       {/* Controls Bar */}
//       <div style={{
//         display: "flex",
//         flexWrap: "wrap",
//         gap: "12px",
//         marginBottom: "12px",
//         padding: "12px",
//         background: "#f8fafc",
//         borderRadius: "10px",
//         border: "1px solid #e2e8f0",
//       }}>
//         <input
//           type="text"
//           placeholder="Search by ID, Suco, Post Admin, Municipality..."
//           value={searchTerm}
//           onChange={(e) => setSearchTerm(e.target.value)}
//           style={{
//             padding: "8px 12px",
//             border: "1px solid #cbd5e1",
//             borderRadius: "8px",
//             fontSize: "13px",
//             flex: "1 1 280px",
//             minWidth: "280px",
//           }}
//         />
//         <select
//           value={rowsPerPage}
//           onChange={(e) => {
//             setRowsPerPage(Number(e.target.value));
//             setCurrentPage(1);
//           }}
//           style={{
//             padding: "8px 12px",
//             border: "1px solid #cbd5e1",
//             borderRadius: "8px",
//             fontSize: "13px",
//             background: "white",
//           }}
//         >
//           <option value={10}>10 rows</option>
//           <option value={25}>25 rows</option>
//           <option value={50}>50 rows</option>
//           <option value={100}>100 rows</option>
//         </select>
//         <button
//           onClick={exportToCSV}
//           style={{
//             padding: "8px 16px",
//             background: "#0d6efd",
//             color: "white",
//             border: "none",
//             borderRadius: "8px",
//             fontSize: "13px",
//             fontWeight: 600,
//             cursor: "pointer",
//           }}
//         >
//           Export CSV
//         </button>
//         <div style={{ position: "relative" }}>
//           <button
//             onClick={() => {
//               const dropdown = document.getElementById("column-dropdown");
//               dropdown.style.display = dropdown.style.display === "block" ? "none" : "block";
//             }}
//             style={{
//               padding: "8px 16px",
//               background: "#64748b",
//               color: "white",
//               border: "none",
//               borderRadius: "8px",
//               fontSize: "13px",
//               fontWeight: 600,
//               cursor: "pointer",
//             }}
//           >
//             Show/Hide Columns
//           </button>
//           <div
//             id="column-dropdown"
//             style={{
//               display: "none",
//               position: "absolute",
//               top: "100%",
//               right: 0,
//               marginTop: "4px",
//               background: "white",
//               border: "1px solid #cbd5e1",
//               borderRadius: "8px",
//               padding: "8px",
//               boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
//               zIndex: 1000,
//               minWidth: "220px",
//             }}
//           >
//             {Object.keys(visibleColumns).map(col => (
//               <label key={col} style={{ display: "block", padding: "4px 8px", fontSize: "13px", cursor: "pointer" }}>
//                 <input
//                   type="checkbox"
//                   checked={visibleColumns[col]}
//                   onChange={(e) => setVisibleColumns({ ...visibleColumns, [col]: e.target.checked })}
//                   style={{ marginRight: "8px" }}
//                 />
//                 {col === "adm3" ? "Suco" :
//                  col === "adm2" ? "Post Administrative" :
//                  col === "adm1" ? "Municipality" :
//                  col.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
//               </label>
//             ))}
//           </div>
//         </div>
//       </div>

//       {/* Table */}
//       <div style={{ overflowX: "auto", border: "1px solid #e2e8f0", borderRadius: "10px" }}>
//         <table style={{
//           width: "100%",
//           borderCollapse: "collapse",
//           fontSize: "13px",
//           background: "white",
//         }}>
//           <thead>
//             <tr style={{ background: "#f1f5f9" }}>
//               {visibleColumns.interval && (
//                 <th onClick={() => handleSort("interval")} style={{ border: "1px solid #e2e8f0", padding: "12px 10px", textAlign: "left", fontWeight: 700, cursor: "pointer" }}>
//                   Interval {sortConfig.key === "interval" && (sortConfig.direction === "asc" ? "↑" : "↓")}
//                 </th>
//               )}
//               {visibleColumns.observation_id && (
//                 <th onClick={() => handleSort("observation_id")} style={{ border: "1px solid #e2e8f0", padding: "12px 10px", textAlign: "left", fontWeight: 700, cursor: "pointer" }}>
//                   Observation ID {sortConfig.key === "observation_id" && (sortConfig.direction === "asc" ? "↑" : "↓")}
//                 </th>
//               )}
//               {visibleColumns.adm3 && (
//                 <th onClick={() => handleSort("adm3")} style={{ border: "1px solid #e2e8f0", padding: "12px 10px", textAlign: "left", fontWeight: 700, cursor: "pointer" }}>
//                   Suco {sortConfig.key === "adm3" && (sortConfig.direction === "asc" ? "↑" : "↓")}
//                 </th>
//               )}
//               {visibleColumns.adm2 && (
//                 <th onClick={() => handleSort("adm2")} style={{ border: "1px solid #e2e8f0", padding: "12px 10px", textAlign: "left", fontWeight: 700, cursor: "pointer" }}>
//                   Post Administrative {sortConfig.key === "adm2" && (sortConfig.direction === "asc" ? "↑" : "↓")}
//                 </th>
//               )}
//               {visibleColumns.adm1 && (
//                 <th onClick={() => handleSort("adm1")} style={{ border: "1px solid #e2e8f0", padding: "12px 10px", textAlign: "left", fontWeight: 700, cursor: "pointer" }}>
//                   Municipality {sortConfig.key === "adm1" && (sortConfig.direction === "asc" ? "↑" : "↓")}
//                 </th>
//               )}
//               {visibleColumns.pulse_type && (
//                 <th onClick={() => handleSort("pulse_type")} style={{ border: "1px solid #e2e8f0", padding: "12px 10px", textAlign: "left", fontWeight: 700, cursor: "pointer" }}>
//                   Pulse Type {sortConfig.key === "pulse_type" && (sortConfig.direction === "asc" ? "↑" : "↓")}
//                 </th>
//               )}
//               {visibleColumns.peak_amplitude && (
//                 <th onClick={() => handleSort("peak_amplitude")} style={{ border: "1px solid #e2e8f0", padding: "12px 10px", textAlign: "left", fontWeight: 700, cursor: "pointer" }}>
//                   Peak Amplitude {sortConfig.key === "peak_amplitude" && (sortConfig.direction === "asc" ? "↑" : "↓")}
//                 </th>
//               )}
//               {visibleColumns.number_sensors && (
//                 <th onClick={() => handleSort("number_sensors")} style={{ border: "1px solid #e2e8f0", padding: "12px 10px", textAlign: "left", fontWeight: 700, cursor: "pointer" }}>
//                   Number Sensors {sortConfig.key === "number_sensors" && (sortConfig.direction === "asc" ? "↑" : "↓")}
//                 </th>
//               )}
//               {visibleColumns.lat && (
//                 <th onClick={() => handleSort("lat")} style={{ border: "1px solid #e2e8f0", padding: "12px 10px", textAlign: "left", fontWeight: 700, cursor: "pointer" }}>
//                   Latitude {sortConfig.key === "lat" && (sortConfig.direction === "asc" ? "↑" : "↓")}
//                 </th>
//               )}
//               {visibleColumns.lng && (
//                 <th onClick={() => handleSort("lng")} style={{ border: "1px solid #e2e8f0", padding: "12px 10px", textAlign: "left", fontWeight: 700, cursor: "pointer" }}>
//                   Longitude {sortConfig.key === "lng" && (sortConfig.direction === "asc" ? "↑" : "↓")}
//                 </th>
//               )}
//               {visibleColumns.created_at && (
//                 <th onClick={() => handleSort("created_at")} style={{ border: "1px solid #e2e8f0", padding: "12px 10px", textAlign: "left", fontWeight: 700, cursor: "pointer" }}>
//                   Created At {sortConfig.key === "created_at" && (sortConfig.direction === "asc" ? "↑" : "↓")}
//                 </th>
//               )}
//               {visibleColumns.date_hr && (
//                 <th onClick={() => handleSort("date_hr")} style={{ border: "1px solid #e2e8f0", padding: "12px 10px", textAlign: "left", fontWeight: 700, cursor: "pointer" }}>
//                   Date HR {sortConfig.key === "date_hr" && (sortConfig.direction === "asc" ? "↑" : "↓")}
//                 </th>
//               )}
//             </tr>
//           </thead>
//           <tbody>
//             {paginatedData.length === 0 ? (
//               <tr>
//                 <td colSpan={Object.values(visibleColumns).filter(Boolean).length} style={{ border: "1px solid #e2e8f0", padding: "20px", textAlign: "center", color: "#64748b" }}>
//                   No data available
//                 </td>
//               </tr>
//             ) : (
//               paginatedData.map((row, idx) => {
//                 const cfg = BUCKET_CONFIG[row.bucket];
//                 return (
//                   <tr key={idx} style={{ borderBottom: "1px solid #f1f5f9" }}>
//                     {visibleColumns.interval && (
//                       <td style={{ border: "1px solid #e2e8f0", padding: "10px" }}>
//                         <div style={{ display: "inline-flex", flexDirection: "column", background: "white", border: `2px solid ${cfg?.color || "#9ca3af"}`, borderRadius: "8px", padding: "4px 10px", minWidth: "120px" }}>
//                           <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
//                             <svg width="10" height="10" viewBox="0 0 24 24" fill={cfg?.color || "#9ca3af"}>
//                               <path d="M13 2 L3 14 L12 14 L11 22 L21 10 L12 10 L13 2 Z" />
//                             </svg>
//                             <span style={{ fontSize: "11px", fontWeight: 600, color: "#111" }}>
//                               {cfg?.label || row.bucket}
//                             </span>
//                           </div>
//                         </div>
//                       </td>
//                     )}
//                     {visibleColumns.observation_id && <td style={{ border: "1px solid #e2e8f0", padding: "10px", color: "#334155" }}>{row.observation_id || "-"}</td>}
//                     {visibleColumns.adm3 && <td style={{ border: "1px solid #e2e8f0", padding: "10px", color: "#334155", fontWeight: 500 }}>{row.adm3 || "-"}</td>}
//                     {visibleColumns.adm2 && <td style={{ border: "1px solid #e2e8f0", padding: "10px", color: "#334155" }}>{row.adm2 || "-"}</td>}
//                     {visibleColumns.adm1 && <td style={{ border: "1px solid #e2e8f0", padding: "10px", color: "#334155" }}>{row.adm1 || "-"}</td>}
//                     {visibleColumns.pulse_type && <td style={{ border: "1px solid #e2e8f0", padding: "10px", color: "#334155" }}>{row.pulse_type?.toUpperCase() || "-"}</td>}
//                     {visibleColumns.peak_amplitude && <td style={{ border: "1px solid #e2e8f0", padding: "10px", color: "#334155" }}>{row.peak_amplitude || "-"}</td>}
//                     {visibleColumns.number_sensors && <td style={{ border: "1px solid #e2e8f0", padding: "10px", color: "#334155" }}>{row.number_sensors || "-"}</td>}
//                     {visibleColumns.lat && <td style={{ border: "1px solid #e2e8f0", padding: "10px", color: "#334155" }}>{row.lat || "-"}</td>}
//                     {visibleColumns.lng && <td style={{ border: "1px solid #e2e8f0", padding: "10px", color: "#334155" }}>{row.lng || "-"}</td>}
//                     {visibleColumns.created_at && (
//                       <td style={{ border: "1px solid #e2e8f0", padding: "10px", color: "#334155" }}>
//                         {row.created_at ? new Date(row.created_at).toLocaleString() : "-"}
//                       </td>
//                     )}
//                     {visibleColumns.date_hr && <td style={{ border: "1px solid #e2e8f0", padding: "10px", color: "#334155" }}>{row.date_hr || "-"}</td>}
//                   </tr>
//                 );
//               })
//             )}
//           </tbody>
//         </table>
//       </div>

//       {/* Pagination */}
//       {totalPages > 1 && (
//         <div style={{
//           display: "flex",
//           justifyContent: "space-between",
//           alignItems: "center",
//           marginTop: "12px",
//           padding: "10px 12px",
//           background: "#f8fafc",
//           borderRadius: "10px",
//           border: "1px solid #e2e8f0",
//         }}>
//           <div style={{ fontSize: "13px", color: "#64748b" }}>
//             Showing {((currentPage - 1) * rowsPerPage) + 1} to {Math.min(currentPage * rowsPerPage, filteredData.length)} of {filteredData.length} entries
//           </div>
//           <div style={{ display: "flex", gap: "4px" }}>
//             <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} style={{ padding: "6px 12px", border: "1px solid #cbd5e1", borderRadius: "6px", background: currentPage === 1 ? "#f1f5f9" : "white", cursor: currentPage === 1 ? "not-allowed" : "pointer" }}>First</button>
//             <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} style={{ padding: "6px 12px", border: "1px solid #cbd5e1", borderRadius: "6px", background: currentPage === 1 ? "#f1f5f9" : "white", cursor: currentPage === 1 ? "not-allowed" : "pointer" }}>Previous</button>
//             <span style={{ padding: "6px 12px", fontSize: "13px", color: "#334155", fontWeight: 600 }}>Page {currentPage} of {totalPages}</span>
//             <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} style={{ padding: "6px 12px", border: "1px solid #cbd5e1", borderRadius: "6px", background: currentPage === totalPages ? "#f1f5f9" : "white", cursor: currentPage === totalPages ? "not-allowed" : "pointer" }}>Next</button>
//             <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} style={{ padding: "6px 12px", border: "1px solid #cbd5e1", borderRadius: "6px", background: currentPage === totalPages ? "#f1f5f9" : "white", cursor: currentPage === totalPages ? "not-allowed" : "pointer" }}>Last</button>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

const NowcastTable = ({ tableData, loading, currentTimestamp, bucketCounts }) => {
  const columns = useMemo(() => [
    {
      key: 'interval',
      label: 'Interval',
      sortable: true,
      align: 'left',
      render: (row) => {
        const cfg = BUCKET_CONFIG[row.bucket];
        return (
          <div style={{ 
            display: "inline-flex", 
            flexDirection: "column", 
            background: "white", 
            border: `2px solid ${cfg?.color || "#9ca3af"}`, 
            borderRadius: "8px", 
            padding: "4px 10px", 
            minWidth: "120px" 
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <svg width="10" height="10" viewBox="0 0 24 24" fill={cfg?.color || "#9ca3af"}>
                <path d="M13 2 L3 14 L12 14 L11 22 L21 10 L12 10 L13 2 Z" />
              </svg>
              <span style={{ fontSize: "11px", fontWeight: 600, color: "#111" }}>
                {cfg?.label || row.bucket}
              </span>
            </div>
          </div>
        );
      }
    },
    {
      key: 'observation_id',
      label: 'Observation ID',
      sortable: true,
      align: 'left',
    },
    {
      key: 'adm3',
      label: 'Suco',
      sortable: true,
      align: 'left',
      render: (row) => (
        <span style={{ fontWeight: 500, color: '#334155' }}>{row.adm3 || '-'}</span>
      )
    },
    {
      key: 'adm2',
      label: 'Post Administrative',
      sortable: true,
      align: 'left',
    },
    {
      key: 'adm1',
      label: 'Municipality',
      sortable: true,
      align: 'left',
    },
    {
      key: 'pulse_type',
      label: 'Pulse Type',
      sortable: true,
      align: 'left',
      render: (row) => (
        <span style={{ color: '#334155' }}>{row.pulse_type?.toUpperCase() || '-'}</span>
      )
    },
    {
      key: 'peak_amplitude',
      label: 'Peak Amplitude',
      sortable: true,
      align: 'left',
    },
    {
      key: 'number_sensors',
      label: 'Number Sensors',
      sortable: true,
      align: 'left',
    },
    {
      key: 'lat',
      label: 'Latitude',
      sortable: true,
      align: 'left',
    },
    {
      key: 'lng',
      label: 'Longitude',
      sortable: true,
      align: 'left',
    },
    {
      key: 'created_at',
      label: 'Created At',
      sortable: true,
      align: 'left',
      render: (row) => (
        <span style={{ color: '#334155' }}>
          {row.created_at ? new Date(row.created_at).toLocaleString() : '-'}
        </span>
      )
    },
    {
      key: 'date_hr',
      label: 'Date HR',
      sortable: true,
      align: 'left',
    },
  ], []);
 
  const searchableKeys = [
    'observation_id',
    'adm3',
    'adm2',
    'adm1',
    'pulse_type',
    'peak_amplitude',
    'date_hr'
  ];
 
  return (
    <div style={{ marginTop: "10px" }}>
      <CommonTable
        tableData={tableData}
        columns={columns}
        currentTimestamp={currentTimestamp}
        showFilter={false}
        searchableKeys={searchableKeys}
        tableName="Nowcast_Data"
        searchPlaceholder="Search by ID, Suco, Post Admin, Municipality..."
        emptyMessage="No data available"
      />
    </div>
  );
};
 

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────
const Nowcast = () => {
  const [loading, setLoading] = useState(true);
  const [nowcastBuckets, setNowcastBuckets] = useState({});
  const [forecasts, setForecasts] = useState([]);
  const [adm1Data, setAdm1Data] = useState(null);
  const [, setAdm2Data] = useState(null);
  const [adm3Data, setAdm3Data] = useState(null);
  const [allSucos, setAllSucos] = useState([]);
  const [zoomFeature, setZoomFeature] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(() => floorTo5Min(getTLNow()));
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [tableView, setTableView] = useState("nowcast");
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [, setIsGeneratingReport] = useState(false);
  const [showAllTooltips, setShowAllTooltips] = useState(false);
  const [, setLastUpdated] = useState(null);

  const mapRef = useRef(null);
  const datePickerRef = useRef(null);
  const autoRefRef = useRef(null);

  const minTLDate = useMemo(() => {
    const tlNow = getTLNow();
    const d = new Date(Date.UTC(tlNow.getUTCFullYear(), tlNow.getUTCMonth(), tlNow.getUTCDate()));
    d.setUTCDate(d.getUTCDate() - 30);
    return d;
  }, []);

  const getMaxSlot = () => floorTo5Min(getTLNow());

  useEffect(() => {
    const h = (e) => {
      if (datePickerRef.current && !datePickerRef.current.contains(e.target)) {
        setShowDatePicker(false);
      }
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [a1, a2, a3, sucos] = await Promise.all([
          fetch("/DATA/OSMbased_adm1_boundary.json").then(r => r.json()),
          fetch("/DATA/OSMbased_adm2_boundary.json").then(r => r.json()),
          fetch("/DATA/OSMbased_adm3_boundary.json").then(r => r.json()),
          axios.post(`${backendUrl}Municipalities/suco_list/`),
        ]);
        setAdm1Data(a1);
        setAdm2Data(a2);
        setAdm3Data(a3);
        setAllSucos(sucos.data);
        if (a1?.features) setZoomFeature(a1);
      } catch (err) {
        console.error("Static data error:", err);
      }
    })();
  }, []);

  const fetchData = useCallback(async (slot) => {
    if (!slot) return;
    setLoading(true);
    try {
      const dtParam = formatSlotForApi(slot);
      const dayStr = slot.toISOString().split("T")[0];
      const [obsRes, fcastRes] = await Promise.all([
        axios.get(`${backendUrl}lightning/nowcast/?datetime=${dtParam}`),
        axios.get(`${backendUrl}lightning/lightning-threats/?date=${dayStr}`),
      ]);
      const bMap = {};
      (obsRes.data || []).forEach(b => {
        bMap[b.bucket] = {
          data: b.data || [],
          count: b.count || { total: 0, cg_count: 0, ic_count: 0 }
        };
      });
      setNowcastBuckets(bMap);
      setForecasts(fcastRes.data || []);
      setLastUpdated(new Date());
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(selectedSlot);
  }, [selectedSlot, fetchData]);

  useEffect(() => {
    clearInterval(autoRefRef.current);
    autoRefRef.current = setInterval(() => {
      const newMax = getMaxSlot();
      setSelectedSlot(prev => {
        const prevMax = new Date(newMax.getTime() - 5 * 60000);
        if (prev.getTime() >= prevMax.getTime()) return newMax;
        return prev;
      });
    }, 300000);
    return () => clearInterval(autoRefRef.current);
  }, []);

  const bucketCounts = useMemo(() => {
    const res = {};
    Object.keys(BUCKET_CONFIG).forEach(k => {
      const b = nowcastBuckets[k];
      res[k] = b
        ? {
            total: b.count?.total ?? b.data.length,
            cg: b.count?.cg_count ?? b.data.filter(d => d.pulse_type === "cg").length,
            ic: b.count?.ic_count ?? b.data.filter(d => d.pulse_type === "ic").length
          }
        : { total: 0, cg: 0, ic: 0 };
    });
    return res;
  }, [nowcastBuckets]);

  const past24Exclusive = useMemo(() => {
    const seen = new Set(
      ["5mins", "10-5min", "15-10min", "30-15min"].flatMap(
        k => (nowcastBuckets[k]?.data || []).map(d => d.observation_id)
      )
    );
    return (nowcastBuckets["24hours"]?.data || []).filter(d => !seen.has(d.observation_id));
  }, [nowcastBuckets]);

  const currentForecasts = useMemo(() => {
    const day = selectedSlot.toISOString().split("T")[0];
    return forecasts.filter(d => d.date_hr?.startsWith(day));
  }, [forecasts, selectedSlot]);

  const nowcastTableData = useMemo(() => {
    const result = [];
    const bucketsOrder = ["5mins", "10-5min", "15-10min", "30-15min"];
    const recentIds = new Set();

    bucketsOrder.forEach((bucket) => {
      const bucketData = nowcastBuckets[bucket]?.data || [];
      bucketData.forEach((obs) => {
        recentIds.add(obs.observation_id);
        result.push({ ...obs, bucket });
      });
    });

    const past24 = nowcastBuckets["24hours"]?.data || [];
    past24.forEach((obs) => {
      if (!recentIds.has(obs.observation_id)) {
        result.push({ ...obs, bucket: "24hours" });
      }
    });

    return result;
  }, [nowcastBuckets]);

  const threatsTableData = useMemo(() => {
    const rows = [];
    ["5mins", "10-5min", "15-10min", "30-15min"].forEach((b) => {
      (nowcastBuckets[b]?.data || []).forEach((obs) => {
        if (!obs.admcode) return;
        const suco = allSucos.find((s) => s.adm3_code === obs.admcode);
        if (!suco) return;
        const feat = adm3Data?.features?.find((f) => f.properties.ADM_Code === obs.admcode);
        const props = feat?.properties || {};
        rows.push({
          bucket: b,
          type: "Observation",
          threatType: "Actual Strike",
          admcode: obs.admcode,
          count: 1,
          advisory: "Low",
          peak: Math.abs(obs.peak_amplitude || 0),
          date_hr: obs.date_hr,
          Municipality: suco.adm1,
          PostAdmin: suco.adm2,
          Suco: suco.adm3,
          Population: props.Population || 0,
          Male: props.Male || 0,
          Female: props.Female || 0,
          Children: props.HH_Urban || 0,
          Elderly: props.HH_Rural || 0,
          Household: props.Household || 0,
        });
      });
    });

    past24Exclusive.forEach((obs) => {
      if (!obs.admcode) return;
      const suco = allSucos.find((s) => s.adm3_code === obs.admcode);
      if (!suco) return;
      const feat = adm3Data?.features?.find((f) => f.properties.ADM_Code === obs.admcode);
      const props = feat?.properties || {};
      rows.push({
        bucket: "24hours",
        type: "Observation",
        threatType: "Actual Strike",
        admcode: obs.admcode,
        count: 1,
        advisory: "Low",
        peak: Math.abs(obs.peak_amplitude || 0),
        date_hr: obs.date_hr,
        Municipality: suco.adm1,
        PostAdmin: suco.adm2,
        Suco: suco.adm3,
        Population: props.Population || 0,
        Male: props.Male || 0,
        Female: props.Female || 0,
        Children: props.HH_Urban || 0,
        Elderly: props.HH_Rural || 0,
        Household: props.Household || 0,
      });
    });

    currentForecasts.forEach((fcast) => {
      const geom = fcast.geometry;
      if (!geom) return;
      const addThreat = (code, threatType) => {
        const suco = allSucos.find((s) => s.adm3_code === code);
        if (!suco) return;
        const feat = adm3Data?.features?.find((f) => f.properties.ADM_Code === code);
        const props = feat?.properties || {};
        rows.push({
          bucket: "threat",
          type: "Threat",
          threatType,
          admcode: code,
          count: 1,
          advisory: fcast.reliability || "Low",
          peak: 0,
          date_hr: fcast.date_hr,
          Municipality: suco.adm1,
          PostAdmin: suco.adm2,
          Suco: suco.adm3,
          Population: props.Population || 0,
          Male: props.Male || 0,
          Female: props.Female || 0,
          Children: props.HH_Urban || 0,
          Elderly: props.HH_Rural || 0,
          Household: props.Household || 0,
        });
      };

      if (geom.type === "Point") {
        const pt = turf.point(geom.coordinates);
        const f = adm3Data?.features?.find((f) => turf.booleanPointInPolygon(pt, f));
        if (f) addThreat(f.properties.ADM_Code, "Storm Centroid");
      } else if (geom.type === "Polygon") {
        const poly = turf.polygon(geom.coordinates);
        adm3Data?.features?.forEach((f) => {
          if (turf.booleanIntersects(poly, f)) addThreat(f.properties.ADM_Code, "Storm Coverage");
        });
      } else if (geom.type === "LineString") {
        const line = turf.lineString(geom.coordinates);
        adm3Data?.features?.forEach((f) => {
          if (turf.booleanIntersects(line, f)) addThreat(f.properties.ADM_Code, "Storm Track");
        });
      }
    });
    return rows;
  }, [nowcastBuckets, past24Exclusive, currentForecasts, allSucos, adm3Data]);

  const handlePrev = () => {
    const prev = new Date(selectedSlot.getTime() - 5 * 60000);
    if (prev.getTime() >= minTLDate.getTime()) setSelectedSlot(prev);
  };

  const handleNext = () => {
    const next = new Date(selectedSlot.getTime() + 5 * 60000);
    const max = getMaxSlot();
    if (next.getTime() <= max.getTime()) setSelectedSlot(next);
  };

  const isAtMax = selectedSlot.getTime() >= getMaxSlot().getTime();

  const handleDateTimeChange = (date) => {
    if (!date) return;
    let utc = new Date(Date.UTC(
      date.getFullYear(), date.getMonth(), date.getDate(),
      date.getHours(), Math.floor(date.getMinutes() / 5) * 5, 0, 0
    ));
    const maxSlot = getMaxSlot();
    if (utc > maxSlot) utc = maxSlot;
    if (utc < minTLDate) utc = minTLDate;
    setSelectedSlot(utc);
  };

const getTimeLimits = () => {
  const nowTL = getTLNow();
  const selectedDate = new Date(
    selectedSlot.getUTCFullYear(),
    selectedSlot.getUTCMonth(),
    selectedSlot.getUTCDate()
  );

  const isToday =
    selectedDate.getUTCFullYear() === nowTL.getUTCFullYear() &&
    selectedDate.getUTCMonth() === nowTL.getUTCMonth() &&
    selectedDate.getUTCDate() === nowTL.getUTCDate();

  if (isToday) {
    // Today: from 00:00 to current time (floored to 5 minutes)
    const minTimeVal = new Date(selectedDate.getTime()); // 00:00

    const currentMinutes = Math.floor(nowTL.getUTCMinutes() / 5) * 5;
    const maxTimeVal = new Date(
      selectedDate.getTime() +
      (nowTL.getUTCHours() * 3600000) +
      (currentMinutes * 60000)
    );

    return { minTime: minTimeVal, maxTime: maxTimeVal };
  } else {
    // Previous days: full day 00:00 to 23:55
    const minTimeVal = new Date(selectedDate.getTime());
    const maxTimeVal = new Date(selectedDate.getTime() + 23 * 3600000 + 55 * 60000);

    return { minTime: minTimeVal, maxTime: maxTimeVal };
  }
};

  const { minTime, maxTime } = getTimeLimits();

  const handleLanguageSelectAndGenerate = async (lang) => {
    setShowLanguageModal(false);
    await captureAndGeneratePdf({
      moduleName: "Nowcast",
      language: lang,
      mapRef,
      setIsGeneratingReport,
      currentTimestamp: formatSlotForApi(selectedSlot),
      impactedTableData: tableView === "nowcast" ? nowcastTableData : threatsTableData,
      translations,
      riskTranslations,
      characteristicColors,
    });
  };

  const onEachSucoFeature = (feature, layer) => {
    const props = feature.properties;
    const match = threatsTableData.find(d => d.admcode === props.ADM_Code);
    const advisory = match?.advisory || "None";
    const advColor = advisory.toLowerCase() === "high" ? "#d32f2f"
      : advisory.toLowerCase() === "medium" ? "#f57c00"
      : advisory.toLowerCase() === "low" ? "#388e3c" : "#757575";

    layer.bindPopup(`
      <div style="font-family:Arial,sans-serif;font-size:13px;max-width:270px">
        <h3 style="margin-bottom:4px;font-size:14px">Suco: <b>${props.ADM3||"N/A"}</b></h3>
        <div style="font-size:12px;line-height:1.5">
          <b>Municipality:</b> ${props.ADM1||"N/A"}<br/>
          <b>Post Admin:</b> ${props.ADM2||"N/A"}<br/>
          <div style="margin-top:4px;font-weight:bold;color:${advColor}">Advisory: ${advisory}</div>
          <hr style="margin:8px 0;border:0;border-top:1px solid #eee"/>
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:4px">
            <span><b>Population:</b> ${props.Population||0}</span>
            <span><b>Household:</b> ${props.Household||0}</span>
            <span><b>Male:</b> ${props.Male||0}</span>
            <span><b>Female:</b> ${props.Female||0}</span>
          </div>
        </div>
      </div>
    `);
    layer.on({
      mouseover: e => e.target.setStyle({ color: "#0d6efd", weight: 2, fillOpacity: 0.1, fillColor: "#0d6efd" }),
      mouseout: e => e.target.setStyle(getBoundaryStyle()),
    });
  };

  // Empty bucket check for notice display
  const emptyBuckets = useMemo(() => {
    return Object.keys(BUCKET_CONFIG).filter(bucket => {
      const count = bucketCounts[bucket];
      return count && count.total === 0;
    });
  }, [bucketCounts]);

  return (
    <div className="fm_nowcast-map-container" style={{ marginBottom: "20px" }}>
      <Box sx={{ position: "relative" }}>
        {/* CONTROL BAR */}
        <Box sx={{
          p: "12px 20px",
          backgroundColor: "#ffffff",
          borderRadius: "16px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
          border: "1px solid #f1f3f5",
          marginBottom: "16px",
          position: "relative",
          zIndex: 10,
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px", position: "relative" }} ref={datePickerRef}>
              <IconButton size="small" onClick={handlePrev} disabled={selectedSlot.getTime() <= minTLDate.getTime()} sx={{ color: "#0d6efd", p: "6px" }}>
                <FontAwesomeIcon icon={faArrowCircleLeft} style={{ fontSize: "22px" }} />
              </IconButton>
              <span style={{
                fontSize: "15px",
                fontWeight: 700,
                color: "#1e293b",
                background: "#f8fafc",
                borderRadius: "12px",
                padding: "8px 16px",
                whiteSpace: "nowrap",
                letterSpacing: "0.3px",
                border: "1px solid #e2e8f0",
              }}>
                {formatSlotDisplay(selectedSlot)}
              </span>
              <IconButton size="small" onClick={() => setShowDatePicker(v => !v)} sx={{ color: "#0d6efd", p: "6px" }} title="Pick date & time">
                <CalendarMonthIcon sx={{ fontSize: "24px" }} />
              </IconButton>
              <IconButton size="small" onClick={handleNext} disabled={isAtMax} sx={{ color: isAtMax ? "#cbd5e1" : "#0d6efd", p: "6px" }}>
                <FontAwesomeIcon icon={faArrowCircleRight} style={{ fontSize: "22px" }} />
              </IconButton>
              {showDatePicker && (
                <div style={{
                  position: "absolute",
                  top: "52px",
                  left: "0",
                  zIndex: 99999,
                  display: "flex",
                  background: "#ffffff",
                  borderRadius: "16px",
                  boxShadow: "0 10px 40px rgba(0,0,0,0.12)",
                  overflow: "hidden",
                  border: "1px solid #f1f5f9",
                }}>
                  <div style={{ padding: "12px 8px 12px 12px" }}>
                    <DatePicker
                      selected={new Date(selectedSlot.getUTCFullYear(), selectedSlot.getUTCMonth(), selectedSlot.getUTCDate())}
                      onChange={handleDateTimeChange}
                      minDate={new Date(minTLDate.getUTCFullYear(), minTLDate.getUTCMonth(), minTLDate.getUTCDate())}
                      maxDate={new Date(getMaxSlot().getUTCFullYear(), getMaxSlot().getUTCMonth(), getMaxSlot().getUTCDate())}
                      inline
                      calendarClassName="modern-flat-calendar"
                    />
                  </div>
                  {/* <div style={{ padding: "12px 12px 12px 8px", borderLeft: "1px solid #f1f5f9" }}>
                    <DatePicker
                      selected={new Date(selectedSlot.getUTCFullYear(), selectedSlot.getUTCMonth(), selectedSlot.getUTCDate(), selectedSlot.getUTCHours(), selectedSlot.getUTCMinutes())}
                      onChange={handleDateTimeChange}
                      showTimeSelect
                      showTimeSelectOnly
                      timeIntervals={5}
                      timeFormat="HH:mm"
                      dateFormat="HH:mm"
                      inline
                      minTime={minTime}
                      maxTime={maxTime}
                      calendarClassName="modern-flat-time"
                    />
                  </div> */}
{/* Time Picker - FIXED */}
<div style={{ padding: "12px 12px 12px 8px", borderLeft: "1px solid #f1f5f9" }}>
  <DatePicker
    selected={
      new Date(
        selectedSlot.getUTCFullYear(),
        selectedSlot.getUTCMonth(),
        selectedSlot.getUTCDate(),
        selectedSlot.getUTCHours(),
        selectedSlot.getUTCMinutes()
      )
    }
    onChange={handleDateTimeChange}
    showTimeSelect
    showTimeSelectOnly
    timeIntervals={5}
    timeFormat="HH:mm"
    dateFormat="HH:mm"
    inline
    minTime={minTime}
    maxTime={maxTime}
    filterTime={(time) => {
      const selectedDate = new Date(
        selectedSlot.getUTCFullYear(),
        selectedSlot.getUTCMonth(),
        selectedSlot.getUTCDate()
      );

      const nowTL = getTLNow();

      const isToday =
        selectedDate.getUTCFullYear() === nowTL.getUTCFullYear() &&
        selectedDate.getUTCMonth() === nowTL.getUTCMonth() &&
        selectedDate.getUTCDate() === nowTL.getUTCDate();

      // Previous days → allow full day (00:00 to 23:55)
      if (!isToday) return true;

      // TODAY → only allow times from 00:00 up to current time
      return time >= minTime && time <= maxTime;
    }}
    timeClassName={(time) => {
      const selectedDate = new Date(
        selectedSlot.getUTCFullYear(),
        selectedSlot.getUTCMonth(),
        selectedSlot.getUTCDate()
      );

      const nowTL = getTLNow();
      const isToday =
        selectedDate.getUTCFullYear() === nowTL.getUTCFullYear() &&
        selectedDate.getUTCMonth() === nowTL.getUTCMonth() &&
        selectedDate.getUTCDate() === nowTL.getUTCDate();

      if (!isToday) return "";

      // Grey out times after current time on today
      if (time > maxTime) {
        return "time-disabled";
      }
      return "";
    }}
    calendarClassName="modern-flat-time"
  />
</div>
                </div>
              )}
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", flex: 1 }}>
              {Object.keys(BUCKET_CONFIG).map(b => {
                const c = bucketCounts[b] || { total: 0, cg: 0, ic: 0 };
                return <BucketBadge key={b} bucket={b} total={c.total} cg={c.cg} ic={c.ic} />;
              })}
            </Box>
            {/* <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
      
              <IconButton size="small" onClick={() => fetchData(selectedSlot)} title="Refresh" sx={{ color: "#0d6efd" }}>
                <RefreshIcon sx={{ fontSize: 22 }} />
              </IconButton>
              <button
                className="generate-report-btn"
                onClick={() => setShowLanguageModal(true)}
                disabled={loading || isGeneratingReport}
                style={{ display: "flex", alignItems: "center", gap: "8px", fontWeight: 600 }}
              >
                {loading || isGeneratingReport ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : <DescriptionIcon style={{ fontSize: "18px" }} />}
                Generate Report
              </button>
            </Box> */}
          </Box>
        </Box>

        {/* MAP */}
        <div className="map-container-lightning">
          <div className="map-wrapper" style={{ position: "relative", zIndex: "1" }}>
            <MapContainer
              ref={mapRef}
              center={[INITIAL_VIEW_STATE.latitude, INITIAL_VIEW_STATE.longitude]}
              zoom={INITIAL_VIEW_STATE.zoom}
              attributionControl={false}
              className="mapcontaineralllightning"
            >
              <MapHomeControl />
              <MapInfoControl text="Lightning nowcast showing recent strikes and short-term forecast risk areas." />
              <LayersControl position="topright">
                <LayersControl.BaseLayer name="Light Map">
                  <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                </LayersControl.BaseLayer>
                <LayersControl.BaseLayer checked name="OpenStreetMap">
                  <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                </LayersControl.BaseLayer>
                {["5mins","10-5min","15-10min","30-15min"].map(bucket => {
                  const cfg = BUCKET_CONFIG[bucket];
                  const data = nowcastBuckets[bucket]?.data || [];
                  return (
                    <LayersControl.Overlay key={bucket} checked={cfg.defaultChecked} name={cfg.layerName}>
                      <LayerGroup>
                        {data.map((obs, idx) => {
                          if (!obs.lat || !obs.lng) return null;
                          return (
                            <Marker
                              key={`${bucket}-${idx}-${showAllTooltips}`}
                              position={[obs.lat, obs.lng]}
                              icon={cfg.icon}
                            >
                              <Tooltip sticky={!showAllTooltips} permanent={showAllTooltips} direction="top" offset={[0,-5]}>
                                <div style={{ fontSize:"11px", lineHeight:1.4, minWidth:"150px" }}>
                                  <div style={{ fontWeight:700, color:cfg.color, marginBottom:"3px", borderBottom:"1px solid #e0e0e0", paddingBottom:"3px" }}>
                                    {cfg.label}
                                  </div>
                                  <div><b>ID:</b> {obs.observation_id?.slice(-8)}</div>
                                  <div><b>Type:</b> {obs.pulse_type?.toUpperCase()}</div>
                                  <div><b>Peak Amplitude:</b> {obs.peak_amplitude}</div>
                                  <div><b>Sensors:</b> {obs.number_sensors}</div>
                                  <div><b>Time:</b> {obs.created_at ? new Date(obs.created_at).toLocaleTimeString() : "—"}</div>
                                </div>
                              </Tooltip>
                            </Marker>
                          );
                        })}
                      </LayerGroup>
                    </LayersControl.Overlay>
                  );
                })}
                <LayersControl.Overlay checked={false} name={BUCKET_CONFIG["24hours"].layerName}>
                  <LayerGroup>
                    {past24Exclusive.map((obs, idx) => {
                      if (!obs.lat || !obs.lng) return null;
                      return (
                        <Marker
                          key={`24h-${idx}-${showAllTooltips}`}
                          position={[obs.lat, obs.lng]}
                          icon={obsIconGrey}
                        >
                          <Tooltip sticky={!showAllTooltips} permanent={showAllTooltips} direction="top" offset={[0,-5]}>
                            <div style={{ fontSize:"11px", lineHeight:1.4, minWidth:"150px" }}>
                              <div style={{ fontWeight:700, color:"#9ca3af", marginBottom:"3px", borderBottom:"1px solid #e0e0e0", paddingBottom:"3px" }}>Past 24 Hours</div>
                              <div><b>Type:</b> {obs.pulse_type?.toUpperCase()}</div>
                              <div><b>Peak:</b> {obs.peak_amplitude}</div>
                              <div><b>Time:</b> {obs.created_at ? new Date(obs.created_at).toLocaleString() : "—"}</div>
                            </div>
                          </Tooltip>
                        </Marker>
                      );
                    })}
                  </LayerGroup>
                </LayersControl.Overlay>
                <LayersControl.Overlay checked name="Lightning Threats">
                  <LayerGroup>
                    {currentForecasts.map((fcast, idx) => {
                      const geom = fcast.geometry;
                      if (!geom) return null;
                      if (geom.type === "Point") {
                        return (
                          <Marker
                            key={`fcast-pt-${idx}-${showAllTooltips}`}
                            position={[geom.coordinates[1], geom.coordinates[0]]}
                            icon={centroidLightningIcon}
                          >
                            <Tooltip sticky={!showAllTooltips} permanent={showAllTooltips} direction="bottom" offset={[0,-10]}>
                              <div style={{ fontSize:"11px", lineHeight:1.4, minWidth:"150px" }}>
                                <div style={{ fontWeight:700, color:"#1565c0", marginBottom:"3px", borderBottom:"1px solid #e0e0e0", paddingBottom:"3px" }}>Storm Centroid</div>
                                <div><b>ID:</b> {fcast.storm_id}</div>
                                <div><b>Reliability:</b> {fcast.reliability}</div>
                              </div>
                            </Tooltip>
                          </Marker>
                        );
                      }
                      if (geom.type === "Polygon") {
                        const positions = geom.coordinates[0].map(c => [c[1], c[0]]);
                        return (
                          <Polygon
                            key={`fcast-poly-${idx}-${showAllTooltips}`}
                            positions={positions}
                            pathOptions={{
                              fillColor: reliabilityColors[fcast.reliability] || reliabilityColors.DEFAULT,
                              color: reliabilityBorders[fcast.reliability] || reliabilityBorders.DEFAULT,
                              weight: 2, fillOpacity: 0.4,
                            }}
                          >
                            <Tooltip sticky={!showAllTooltips} permanent={showAllTooltips} direction="top">
                              <div style={{ fontSize:"11px", lineHeight:1.4, minWidth:"150px" }}>
                                <div style={{ fontWeight:700, color:"#1565c0", marginBottom:"3px", borderBottom:"1px solid #e0e0e0", paddingBottom:"3px" }}>Storm Coverage Area</div>
                                <div><b>ID:</b> {fcast.storm_id}</div>
                                <div><b>Reliability:</b> {fcast.reliability}</div>
                              </div>
                            </Tooltip>
                          </Polygon>
                        );
                      }
                      if (geom.type === "LineString") {
                        const positions = geom.coordinates.map(c => [c[1], c[0]]);
                        return (
                          <Polyline
                            key={`fcast-line-${idx}-${showAllTooltips}`}
                            positions={positions}
                            pathOptions={{ color:"#2563eb", weight:2, dashArray:"5,8" }}
                          >
                            <Tooltip sticky={!showAllTooltips} permanent={showAllTooltips} direction="top" offset={[0,-5]}>
                              <div style={{ fontSize:"11px", lineHeight:1.4, minWidth:"150px" }}>
                                <div style={{ fontWeight:700, color:"#1565c0", marginBottom:"3px", borderBottom:"1px solid #e0e0e0", paddingBottom:"3px" }}>Storm Forecast Track</div>
                                <div><b>ID:</b> {fcast.storm_id}</div>
                                <div><b>Reliability:</b> {fcast.reliability}</div>
                              </div>
                            </Tooltip>
                          </Polyline>
                        );
                      }
                      return null;
                    })}
                  </LayerGroup>
                </LayersControl.Overlay>
                {adm1Data && (
                  <LayersControl.Overlay checked name="Municipality">
                    <GeoJSON data={adm1Data} style={() => getBoundaryStyle()} />
                  </LayersControl.Overlay>
                )}
                {adm3Data && (
                  <LayersControl.Overlay name="Suco">
                    <GeoJSON
                      key={`suco-${showAllTooltips}`}
                      data={adm3Data}
                      style={() => getBoundaryStyle()}
                      onEachFeature={onEachSucoFeature}
                    />
                  </LayersControl.Overlay>
                )}
              </LayersControl>
              {zoomFeature && <ZoomToFeature feature={zoomFeature.features ? zoomFeature : { type:"FeatureCollection", features:[zoomFeature] }} />}
              <div style={{ position:"absolute", bottom:"20px", left:"56px", zIndex:1000 }}>
                <button
                  onClick={() => setShowAllTooltips(v => !v)}
                  style={{
                    display:"flex", alignItems:"center", gap:"8px",
                    padding:"8px 16px",
                    background:"rgba(255,255,255,0.95)", backdropFilter:"blur(12px)",
                    color:"#1e293b", border:"1px solid #e2e8f0",
                    borderRadius:"12px", cursor:"pointer",
                    fontSize:"13px", fontWeight:600,
                    boxShadow:"0 4px 15px rgba(0,0,0,0.1)",
                  }}
                >
                  {showAllTooltips ? "Hide All Labels" : "Show All Labels"}
                </button>
              </div>
              <MapLegendControl
                position="bottomright"
                sections={[
                  {
                    title: "Nowcast Observations",
                    items: Object.keys(BUCKET_CONFIG).map((b) => {
                      const cfg = BUCKET_CONFIG[b];
                      return {
                        label: cfg.label,
                        icon: (
                          <svg width="13" height="13" viewBox="0 0 24 24" fill={cfg.color}>
                            <path d="M13 2 L3 14 L12 14 L11 22 L21 10 L12 10 L13 2 Z" />
                          </svg>
                        ),
                      };
                    }),
                  },
                  {
                    title: "Lightning Threats",
                    items: [
                      {
                        label: "Storm Centroid",
                        icon: (
                          <div style={{ color:"#fbbf24", background:"#111827", borderRadius:"50%", padding:"3px", display:"flex", border:"2px solid #fbbf24" }}>
                            <Zap size={11} fill="currentColor" />
                          </div>
                        ),
                      },
                      { label: "Coverage (Low Reliability)", color: reliabilityBorders.LOW, shape: "square" },
                      { label: "Coverage (Medium Reliability)", color: reliabilityBorders.MEDIUM, shape: "square" },
                      { label: "Coverage (High Reliability)", color: reliabilityBorders.HIGH, shape: "square" },
                      { label: "Storm Path", color: "#2563eb", shape: "line" },
                    ],
                  },
                ]}
              />
            </MapContainer>
          </div>

          {/* TABLE SECTION */}
          <div className="section-buttons-container" style={{ marginTop:"12px", display:"flex", alignItems:"center", gap:"0" }}>
            <button className={`section-button ${tableView === "nowcast" ? "active" : ""}`} onClick={() => setTableView("nowcast")}>Nowcast</button>
            <button className={`section-button ${tableView === "threats" ? "active" : ""}`} onClick={() => setTableView("threats")}>Threats</button>
          </div>

          <div className="section-content-container">
            {tableView === "nowcast" && (
              <>
                {/* Empty Bucket Notices */}
                {emptyBuckets.length > 0 && !loading && (
                  <div style={{ padding: "10px 14px", display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "10px" }}>
                    {emptyBuckets.map(bucket => {
                      const cfg = BUCKET_CONFIG[bucket];
                      return (
                        <div key={bucket} style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                          background: `${cfg.color}12`,
                          border: `2px solid ${cfg.color}`,
                          borderRadius: "10px",
                          padding: "6px 12px",
                          fontSize: "12px",
                          fontWeight: 600,
                          color: "#334155",
                        }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill={cfg.color} style={{ flexShrink: 0 }}>
                            <path d="M13 2 L3 14 L12 14 L11 22 L21 10 L12 10 L13 2 Z" />
                          </svg>
                          <span>No lightning observed in <strong style={{ color: cfg.color }}>{cfg.label.toLowerCase()}</strong></span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Enhanced Nowcast Table */}
                <NowcastTable
                  tableData={nowcastTableData}
                  loading={loading}
                  currentTimestamp={formatSlotDisplay(selectedSlot)}
                  bucketCounts={bucketCounts}
                />
              </>
            )}

            {tableView === "threats" && (
              <ImpactedAreaLN
                loading={loading}
                tableData={threatsTableData}
                currentTimestamp={formatSlotDisplay(selectedSlot)}
                headerDate={formatSlotDisplay(selectedSlot)}
                showThreatType={true}
              />
            )}
          </div>
        </div>
      </Box>

      {/* Language Modal */}
      <Modal show={showLanguageModal} onHide={() => setShowLanguageModal(false)} centered backdrop="static">
        <Modal.Body className="language-modal-body">
          <div className="language-modal-container">
            <div className="language-modal-icon">🌐</div>
            <h3 className="language-modal-title">Select Report Language</h3>
            <p className="language-modal-description">Choose the language for the generated advisory PDF report.</p>
            <div className="language-modal-cards">
              <div onClick={() => handleLanguageSelectAndGenerate("en")} className="language-card-option english">
                <span className="card-emoji">🇬🇧</span><span className="card-label">English</span>
              </div>
              <div onClick={() => handleLanguageSelectAndGenerate("tetum")} className="language-card-option tetum">
                <span className="card-emoji">🇹🇱</span><span className="card-label">Tetum</span>
              </div>
            </div>
            <button onClick={() => setShowLanguageModal(false)} className="language-modal-cancel-btn">Cancel</button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default Nowcast;